import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { isChunkLoadError, reloadOnceForStaleAssets } from '../utils/chunkLoadRecovery';

type Props = { children: ReactNode };
type State = { error: Error | null; reloading: boolean };

class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null, reloading: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    if (isChunkLoadError(error) && reloadOnceForStaleAssets()) {
      this.setState({ reloading: true });
    }
  }

  render() {
    if (this.state.reloading) {
      return <div style={{ padding: 24 }}>Loading…</div>;
    }

    if (!this.state.error) {
      return this.props.children;
    }

    return (
      <div style={{ padding: 32, maxWidth: 480 }}>
        <h1 style={{ fontSize: '1.25rem', marginBottom: 8 }}>This page didn’t load</h1>
        <p style={{ color: '#6b7280', marginBottom: 16 }}>
          A refresh usually fixes this after an app update.
        </p>
        <button type="button" onClick={() => window.location.reload()}>
          Refresh
        </button>
      </div>
    );
  }
}

/** Remounts on navigation so a failed page doesn’t trap the rest of the app. */
export function ErrorBoundary({ children }: Props) {
  const location = useLocation();
  return <RouteErrorBoundary key={location.key}>{children}</RouteErrorBoundary>;
}
